import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DriverList,
  DriverProfile,
  DriverForm
} from '../components/drivers';
import driverService from '../services/driverService';
import { useAuth } from '../contexts/AuthContext';
import ApplicationsTab from '../components/drivers/applications/ApplicationsTab';
import InviteDriverModal from '../components/drivers/applications/InviteDriverModal';

const Drivers = () => {
  const { company, userData, hasAnyRole } = useAuth();
  const canManageApplications = hasAnyRole(['admin', 'manager', 'super_admin']);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [applicationsRefreshKey, setApplicationsRefreshKey] = useState(0);
  const [applicationStats, setApplicationStats] = useState(null);
  const resolvedCompanyId = useMemo(() => {
    if (company?.id) return company.id;
    if (company?.companyId) return company.companyId;
    if (userData?.companyId) return userData.companyId;
    if (userData?.company?.id) return userData.company.id;
    return null;
  }, [company, userData]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // 'list', 'profile', 'form', 'edit'
  const activeTab = searchParams.get('tab') === 'applications' ? 'applications' : 'drivers';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'applications') next.set('tab', 'applications'); else next.delete('tab');
    setSearchParams(next, { replace: true });
  };
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [profileTab, setProfileTab] = useState('personal');
  const [drivers, setDrivers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const handleDriverUpdated = useCallback((updatedDriver) => {
    if (!updatedDriver?.id) {
      return;
    }

    setDrivers((previousDrivers) => {
      if (!Array.isArray(previousDrivers) || !previousDrivers.length) {
        return previousDrivers;
      }

      let didUpdate = false;
      const nextDrivers = previousDrivers.map((existingDriver) => {
        if (existingDriver.id === updatedDriver.id) {
          didUpdate = true;
          return { ...existingDriver, ...updatedDriver };
        }
        return existingDriver;
      });

      return didUpdate ? nextDrivers : previousDrivers;
    });
  }, []);
  const fetchDrivers = useCallback(async () => {
    if (!resolvedCompanyId) {
      setDrivers([]);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await driverService.listDrivers({}, { companyId: resolvedCompanyId });

      // Filter out terminated drivers
      const activeDrivers = result.drivers.filter(driver => driver.status !== 'terminated');

      setDrivers(activeDrivers);
    } catch (error) {
      console.error('Failed to load drivers:', error);
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : 'Failed to load drivers. Please try again.';
      setLoadError(friendlyMessage);
      setDrivers([]);
    } finally {
      setIsLoading(false);
    }
  }, [resolvedCompanyId]);

  // Handle URL parameters for direct navigation to specific actions
  useEffect(() => {
    console.log("useEffect")
    const action = searchParams.get('action');
    if (action === 'add') {
      setView('form');
      setSelectedDriverId(null);
      // Clear the action parameter after handling it
      searchParams.delete('action');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  // Deep link to a profile (?driver=<id>), e.g. right after hiring from an application.
  useEffect(() => {
    const driverParam = searchParams.get('driver');
    if (!driverParam) return;
    setSelectedDriverId(driverParam);
    setProfileTab('personal');
    setView('profile');
    const next = new URLSearchParams(searchParams);
    next.delete('driver');
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleViewDriver = (driverId) => {
    setSelectedDriverId(driverId);
    setProfileTab('personal');
    setView('profile');
  };

  const handleEditDriver = (driverId) => {
    setLoadError(null);
    setSelectedDriverId(driverId);
    setView('edit');
  };

  const handleAddDriver = () => {
    setLoadError(null);
    setSelectedDriverId(null);
    setView('form');
  };

  const handleViewDriverDocuments = (driverId) => {
    setSelectedDriverId(driverId);
    setProfileTab('documents');
    setView('profile');
  };

  const handleViewVehicleDetails = useCallback((vehicleId) => {
    if (!vehicleId) {
      return;
    }

    const params = new URLSearchParams();
    params.set('vehicleId', vehicleId);
    params.set('tab', 'assignments');
    navigate(`/vehicles?${params.toString()}`);
  }, [navigate]);


  const handleBackToList = () => {
    setLoadError(null);
    setSelectedDriverId(null);
    setProfileTab('personal');
    setView('list');
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoadError(null);
      if (view === 'edit') {
        if (!selectedDriverId) {
          throw new Error('Driver selection is missing. Please choose a driver to update.');
        }

        if (!resolvedCompanyId) {
          throw new Error('Company context is missing. Please reload and try again.');
        }

        const currentDriver = getCurrentDriver();
        if (!currentDriver) {
          throw new Error('Unable to locate the selected driver. Please refresh and try again.');
        }

        const updatedDriver = await driverService.updateDriver(selectedDriverId, formData, {
          companyId: resolvedCompanyId,
          defaults: currentDriver
        });
        handleDriverUpdated(updatedDriver);
        await fetchDrivers();
        console.log('Driver updated via API:', updatedDriver);
        handleBackToList();
        return updatedDriver;
      } else {
        if (!resolvedCompanyId) {
          throw new Error('Company context is missing. Please reload and try again.');
        }

        const createdDriver = await driverService.createDriver(formData, { companyId: resolvedCompanyId });
        handleDriverUpdated(createdDriver);
        await fetchDrivers();
        console.log('Driver created via API:', createdDriver);

        // Don't navigate back to list yet - let the form show the documents tab
        // handleBackToList();

        return createdDriver;
      }
    } catch (error) {
      console.error('Error saving driver:', error);
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to save driver. Please try again.');

      const detailMessages = Array.isArray(error?.details)
        ? error.details.map((detail) => {
            if (!detail?.message) {
              return null;
            }
            return detail.message.replace(/["']/g, '').trim();
          }).filter(Boolean)
        : [];

      const messageList = detailMessages.length ? detailMessages : [friendlyMessage];
      setLoadError(messageList);

      if (Array.isArray(messageList)) {
        error.validationMessages = messageList;
      }

      throw error;
    }
  };

  const handleNavigateToDocuments = (driver) => {
    if (!driver?.id) {
      return;
    }
    // Navigate to the driver profile's documents tab
    setSelectedDriverId(driver.id);
    setProfileTab('documents');
    setView('profile');
  };

  const handleDeleteDriver = async (driverId) => {
    try {
      setLoadError(null);

      if (!resolvedCompanyId) {
        throw new Error('Company context is missing. Please reload and try again.');
      }

      await driverService.deleteDriver(driverId, { companyId: resolvedCompanyId });

      // Refresh the driver list
      await fetchDrivers();

      console.log('Driver deleted successfully:', driverId);
    } catch (error) {
      console.error('Error deleting driver:', error);
      const friendlyMessage = typeof error?.getUserFriendlyMessage === 'function'
        ? error.getUserFriendlyMessage()
        : (error?.message || 'Failed to delete driver. Please try again.');

      setLoadError([friendlyMessage]);
    }
  };

  const getCurrentDriver = () => {
    return drivers.find(driver => driver.id === selectedDriverId);
  };

  // Render based on current view - Mobile-friendly header with back button
  const renderFixedHeader = (title, subtitle) => (
    <div className="sticky top-0 z-1 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
        {/* Mobile: Stack vertically, Desktop: Side by side */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          {/* Back button */}
          <div className="flex-shrink-0">
            <button
              onClick={handleBackToList}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden xs:inline">Back to Driver List</span>
              <span className="xs:hidden">Back</span>
            </button>
          </div>
          
          {/* Title section */}
          {title && (
            <div className="flex-1 min-w-0 sm:ml-4">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{title}</h1>
              {subtitle && (
                <p className="text-sm text-gray-600 mt-1 sm:mt-0 line-clamp-2 sm:line-clamp-1">
                  {subtitle}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (view === 'profile') {
    const currentDriver = getCurrentDriver();
    return (
      <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 h-[calc(100vh-4rem)] flex flex-col bg-slate-50">
        {renderFixedHeader(
          `${currentDriver?.name || 'Driver'} Profile`,
          'View and manage driver information'
        )}
        <div className="flex-1 overflow-y-auto">
          <DriverProfile
            driverId={selectedDriverId}
            initialDriver={currentDriver}
            onDriverUpdated={handleDriverUpdated}
            initialTab={profileTab}
          />
        </div>
      </div>
    );
  }

  if (view === 'form' || view === 'edit') {
    const currentDriver = view === 'edit' ? getCurrentDriver() : null;
    return (
      <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 h-[calc(100vh-4rem)] flex flex-col bg-slate-50">
        {renderFixedHeader(
          view === 'edit' ? `Edit ${currentDriver?.name || 'Driver'}` : 'Add New Driver',
          view === 'edit' ? 'Update driver information' : 'Create a new driver profile'
        )}
        <div className="flex-1 overflow-y-auto">
          <DriverForm
            driver={currentDriver}
            onSubmit={handleFormSubmit}
            onCancel={handleBackToList}
            serverError={loadError}
            onNavigateToDocuments={handleNavigateToDocuments}
          />
        </div>
      </div>
    );
  }

  // Default list view
  if (isLoading) {
    return (
      <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 h-[calc(100vh-4rem)] flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="mt-3 text-sm text-gray-600">Loading drivers…</p>
        </div>
      </div>
    );
  }

  const tabStrip = canManageApplications && (
    <div className="border-b border-gray-200 bg-white">
      <nav className="max-w-7xl mx-auto flex gap-6 px-4 sm:px-6 lg:px-8" aria-label="Drivers sections">
        {[
          { id: 'drivers', label: 'Drivers', count: drivers.length },
          { id: 'applications', label: 'Applications', count: applicationStats?.open ?? null, attention: applicationStats?.needsReview || 0 }
        ].map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              aria-current={active ? 'page' : undefined}
              className={`-mb-px flex items-center gap-2 border-b-2 py-3 text-sm font-medium ${active ? 'border-brand-mid text-brand-dark' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'}`}
            >
              {tab.label}
              {typeof tab.count === 'number' && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">{tab.count}</span>}
              {tab.attention > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" title="Waiting for review">{tab.attention} to review</span>}
            </button>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="-mx-4 -my-8 sm:-mx-6 lg:-mx-8 h-[calc(100vh-4rem)] flex flex-col bg-slate-50">
      {tabStrip}
      <div className="min-h-0 flex-1">
        {activeTab === 'applications' && canManageApplications ? (
          <ApplicationsTab
            onInvite={() => setInviteOpen(true)}
            refreshKey={applicationsRefreshKey}
            onStats={setApplicationStats}
          />
        ) : (
          <DriverList
            drivers={drivers}
            onViewDriver={handleViewDriver}
            onEditDriver={handleEditDriver}
            onAddDriver={handleAddDriver}
            onInviteDriver={canManageApplications ? () => setInviteOpen(true) : undefined}
            onViewDocuments={handleViewDriverDocuments}
            onViewVehicle={handleViewVehicleDetails}
            onDeleteDriver={handleDeleteDriver}
          />
        )}
      </div>
      <InviteDriverModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => { setApplicationsRefreshKey((k) => k + 1); }}
      />
    </div>
  );
};

export default Drivers;
